import { ReactElement, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import invariant from 'tiny-invariant'
import { Tabs, Spin, Result } from 'antd'
import tourIds from '../../../tourIds'

import { Page } from '../../common/Page'
import { Participants } from './Participants'
import { Overview } from './Overview'
import { OptionsPage } from './Op2'
import { Valuemetrics } from './Valuemetrics'

import { useQuerystringValue } from '../../../hooks/useQuerystringValue'
import { useAccount } from '../../../context'
import { useDecision2, type UseDecision2Result } from '../../../hooks/useDecision2'
import { useLoadDecision2 } from '../../../redux/hooks'
import { CriteriaPage } from './Cri2'
import { ResultsPage } from './Results'
import { DecisionDownloadButton } from './DecisionDownloadButton'

function getBreadcrumbRoutes(data: UseDecision2Result, accountCommonId: string) {
  return [
    { path: `/${accountCommonId}`, breadcrumbName: 'Home' },
    ...data.ancestors.map(ancestor => ({
      path: `/${accountCommonId}/decision?folder=${ancestor.id}`,
      breadcrumbName: ancestor.name,
    })),
    { path: `/${accountCommonId}/decision/${data.decision.id}`, breadcrumbName: data.decision.name },
  ]
}

function renderTabContents(tabKey: string, decisionId: string) {
  switch (tabKey) {
    case 'overview':
      return <Overview decisionId={decisionId} />
    case 'criteria':
      return <CriteriaPage decisionId={decisionId} />
    case 'options':
      return <OptionsPage decisionId={decisionId} />
    case 'participants':
      return <Participants decisionId={decisionId} />
    case 'option-valuemetrics':
      return <Valuemetrics decisionId={decisionId} />
    case 'results':
      return <ResultsPage decisionId={decisionId} />
    default:
      return null
  }
}

export function DecisionPageContent({ decisionId }: { decisionId: string }): ReactElement {
  const { accountCommonId, accountId } = useAccount()
  const { data: decisionDataLite } = useDecision2({ decisionId, accountId })
  const [tab, setTab] = useQuerystringValue('tab', 'overview', true)

  const tabs = useMemo(
    () =>
      decisionDataLite
        ? [
            // [key, tourId, label]
            ['overview', tourIds.tab.overview, 'Overview'],
            ['criteria', tourIds.tab.criteria, 'Criteria'],
            ['options', tourIds.tab.options, 'Options'],
            ['participants', tourIds.tab.participants, 'Participants'],
            decisionDataLite.decision.decisionType === 'Regular'
              ? ['results', tourIds.tab.results, 'Results']
              : ['option-valuemetrics', tourIds.tab.valuemetrics, 'Valuemetrics'],
          ]
        : [],
    [decisionDataLite]
  )

  useEffect(() => {
    if (!decisionDataLite) return
    const { ancestors, decision } = decisionDataLite
    const path = ancestors.map(a => a.id).join(' / ')
    console.log(`decision: ${path} / ${decision.name} (${decision.id})`)
  }, [decisionDataLite])

  if (!decisionDataLite) return <Spin />

  const breadcrumbRoutes = getBreadcrumbRoutes(decisionDataLite, accountCommonId)

  return (
    <Page
      title={decisionDataLite.decision.name}
      breadcrumbRoutes={breadcrumbRoutes}
      extra={
        decisionDataLite.decision.decisionType !== 'Regular' && <DecisionDownloadButton decisionId={decisionId} />
      }
    >
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={tabs.map(([key, tourId, label]) => ({
          key,
          label: <span id={tourId}>{label}</span>,
        }))}
      />
      {renderTabContents(tab, decisionId)}
    </Page>
  )
}

export function DecisionPage(): ReactElement {
  const { decisionId } = useParams<'decisionId'>()
  invariant(decisionId, 'decisionId required in URL parameters')

  const { accountId } = useAccount()

  const res = useLoadDecision2(accountId, decisionId)

  if (res.status === 'Error')
    return (
      <Result
        status="error"
        title="Error Loading Decision"
        subTitle="Either this decision does not exist or you do not have access."
      />
    )

  if (res.status !== 'Success') return <Spin />

  return <DecisionPageContent decisionId={decisionId} />
}
